import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  TimePicker,
  InputNumber,
  message,
  Divider,
  Row,
  Col,
  Alert,
  Typography,
  Checkbox,
  Spin,
  Modal,
  Descriptions,
  Space,
  Tag
} from 'antd';
import { FileAddOutlined, CalculatorOutlined, CloudUploadOutlined, GoogleOutlined, CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { supabase } from '../../api/supabase';
import dayjs from 'dayjs';
import { getGoogleAuthUrl } from '../../api/googleDrive';
import { generateDocumentWithGoogleDrive, checkGoogleDriveStatus } from '../../utils/googleDocumentGenerator';

const { Text, Title } = Typography;
const { Option } = Select;

const CHARTER_TYPES = [
  { value: 'sbh_sxm', label: 'SBH → SXM', from: '09:00', to: '17:00', cruising: 'St Martin' },
  { value: 'sxm_sbh', label: 'SXM → SBH', from: '09:00', to: '17:00', cruising: 'St Martin' },
  { value: 'sbh_axa', label: 'SBH → AXA', from: '09:00', to: '17:00', cruising: 'Anguilla' },
  { value: 'axa_sbh', label: 'AXA → SBH', from: '09:00', to: '17:00', cruising: 'Anguilla' },
  { value: 'sxm_axa', label: 'SXM → AXA', from: '09:00', to: '17:00', cruising: 'Anguilla' },
  { value: 'axa_sxm', label: 'AXA → SXM', from: '09:00', to: '17:00', cruising: 'St Martin' },
  { value: 'full_day_sbh', label: 'Full Day SBH', from: '09:00', to: '17:00', cruising: 'St Barthélémy' },
  { value: 'full_day_sxm', label: 'Full Day SXM', from: '09:00', to: '17:00', cruising: 'St Martin' },
  { value: 'full_day_axa', label: 'Full Day AXA', from: '09:00', to: '17:00', cruising: 'Anguilla' },
  { value: 'half_day_sbh_am', label: 'Half Day SBH Matin', from: '09:00', to: '13:00', cruising: 'St Barthélémy' },
  { value: 'half_day_sbh_pm', label: 'Half Day SBH Après-midi', from: '13:00', to: '17:00', cruising: 'St Barthélémy' },
  { value: 'sunset', label: 'Sunset', from: '17:00', to: '19:00', cruising: 'St Barthélémy' },
  { value: 'custom', label: 'Personnalisé', from: '09:00', to: '17:00', cruising: '' },
];

const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/gr79uju6w83d2eip7dove0q08sumyvfa';

function MakeDocumentTest() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isTransfer, setIsTransfer] = useState(false);
  const [isDirectCharterer, setIsDirectCharterer] = useState(false);
  const [yachts, setYachts] = useState([]);
  const [responseData, setResponseData] = useState(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [googleStatus, setGoogleStatus] = useState({ configured: false, authenticated: false });
  const [generationMode, setGenerationMode] = useState('make'); // 'make' or 'google'

  useEffect(() => {
    // Check Google Drive status
    const status = checkGoogleDriveStatus();
    setGoogleStatus(status);
  }, []);

  useEffect(() => {
    const fetchYachts = async () => {
      try {
        const { data, error } = await supabase
          .from('yachts')
          .select('*')
          .order('length_feet', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          const formattedYachts = data.map(yacht => ({
            key: yacht.idx || yacht.id,
            brand: yacht.brand || yacht.model,
            name: yacht.name || '',
            length: yacht.length_feet?.toString() || yacht.length?.toString() || '',
            fullData: yacht
          }));
          setYachts(formattedYachts);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des yachts:', error);
      }
    };

    fetchYachts();
  }, []);

  const getVIPPrice = (persons, isNight) => {
    const dayPrices = {
      1: 220, 2: 310, 3: 470, 4: 560, 5: 680, 6: 770, 7: 1010,
      8: 1100, 9: 1200, 10: 1220, 11: 1290, 12: 1390, 13: 1460,
      14: 1540, 15: 1610
    };
    const nightPrices = {
      1: 255, 2: 360, 3: 540, 4: 640, 5: 850, 6: 890, 7: 1200,
      8: 1270, 9: 1380, 10: 1480, 11: 1380, 12: 1600, 13: 1680,
      14: 1770, 15: 1850
    };
    return isNight ? nightPrices[persons] || nightPrices[15] : dayPrices[persons] || dayPrices[15];
  };

  const getTaxiPrice = (persons, isNight) => {
    const dayPrices = {
      1: 60, 2: 60, 3: 80, 4: 90, 5: 110, 6: 120, 7: 140,
      8: 160, 9: 170, 10: 180, 11: 200, 12: 210, 13: 230,
      14: 240, 15: 260
    };
    const nightPrices = {
      1: 70, 2: 70, 3: 90, 4: 110, 5: 130, 6: 140, 7: 160,
      8: 180, 9: 190, 10: 210, 11: 230, 12: 250, 13: 260,
      14: 280, 15: 300
    };
    return isNight ? nightPrices[persons] || nightPrices[15] : dayPrices[persons] || dayPrices[15];
  };

  const autoCalculateTaxes = () => {
    const charterType = form.getFieldValue('charter_type');
    const guestsNumber = form.getFieldValue('guests_number') || 0;

    let taxesTotal = 0;
    let taxesText = '';

    if (!charterType) {
      message.warning('Veuillez sélectionner un type de charter');
      return;
    }

    if (charterType.includes('sxm')) {
      taxesTotal += 200;
      taxesText += 'SXM';
    }

    if (charterType.includes('axa')) {
      taxesTotal += 650;
      taxesText += (taxesText ? ' + ' : '') + 'AXA';
    }

    if (charterType.includes('sbh') && guestsNumber > 0) {
      const sbhTax = guestsNumber * 5;
      taxesTotal += sbhTax;
      taxesText += (taxesText ? ' + ' : '') + `SBH (${guestsNumber}x5€)`;
    }

    if (taxesTotal > 0) {
      form.setFieldsValue({
        taxes_price: taxesTotal,
        taxes_text: taxesText
      });
      calculateTotal();
      message.success(`Taxes calculées: ${taxesTotal}€ (${taxesText})`);
    } else {
      message.info('Aucune taxe pour ce type de charter');
    }
  };

  const autoCalculateVIP = () => {
    const guestsNumber = form.getFieldValue('guests_number') || 0;
    const fromHour = form.getFieldValue('from_hour');

    if (guestsNumber === 0) {
      message.warning('Veuillez entrer le nombre de passagers');
      return;
    }

    let isNight = false;
    if (fromHour) {
      const hour = parseInt(fromHour.format('HH'));
      isNight = hour >= 15;
    }

    const vipPrice = getVIPPrice(guestsNumber, isNight);
    form.setFieldsValue({
      transport_price: vipPrice,
      transport_text: isNight ? 'VIP (Nuit)' : 'VIP'
    });
    calculateTotal();
    message.success(`VIP calculé: ${vipPrice}€ pour ${guestsNumber} pax ${isNight ? '(nuit)' : ''}`);
  };

  const autoCalculateTaxi = () => {
    const guestsNumber = form.getFieldValue('guests_number') || 0;
    const fromHour = form.getFieldValue('from_hour');

    if (guestsNumber === 0) {
      message.warning('Veuillez entrer le nombre de passagers');
      return;
    }

    let isNight = false;
    if (fromHour) {
      const hour = parseInt(fromHour.format('HH'));
      isNight = hour >= 15;
    }

    const taxiPrice = getTaxiPrice(guestsNumber, isNight);
    form.setFieldsValue({
      transport_price: taxiPrice,
      transport_text: isNight ? 'Taxi (Nuit)' : 'Taxi'
    });
    calculateTotal();
    message.success(`Taxi calculé: ${taxiPrice}€ pour ${guestsNumber} pax ${isNight ? '(nuit)' : ''}`);
  };

  const calculateTotal = () => {
    const boatPrice = parseFloat(form.getFieldValue('boat_price')) || 0;
    const transportPrice = parseFloat(form.getFieldValue('transport_price')) || 0;
    const taxesPrice = parseFloat(form.getFieldValue('taxes_price')) || 0;
    const discountPrice = parseFloat(form.getFieldValue('discount_price')) || 0;
    const extraHourPrice = parseFloat(form.getFieldValue('extra_hour_price')) || 0;
    const customPrice = parseFloat(form.getFieldValue('custom_price')) || 0;

    const total = boatPrice + transportPrice + taxesPrice - discountPrice + extraHourPrice + customPrice;

    form.setFieldsValue({
      total_price: total
    });
  };

  const updatePriceAndCruisingArea = () => {
    const yachtKey = form.getFieldValue('yacht');
    const charterType = form.getFieldValue('charter_type');

    if (!yachtKey || !charterType) return;

    const yacht = yachts.find(y => y.key === yachtKey);
    if (!yacht || !yacht.fullData) return;

    const yachtData = yacht.fullData;
    let price = 0;
    let cruisingArea = '';

    switch(charterType) {
      case 'sbh_sxm':
        price = parseFloat(yachtData.day_sbh_sxm_price || yachtData.transfer_st_barth_to_st_martin || 0);
        cruisingArea = 'SBH/SXM Transfer';
        break;
      case 'sxm_sbh':
        price = parseFloat(yachtData.day_sxm_sbh_price || yachtData.transfer_st_martin_to_st_barth || 0);
        cruisingArea = 'SXM/SBH Transfer';
        break;
      case 'sbh_axa':
        price = parseFloat(yachtData.transfer_st_barth_to_anguilla || yachtData.transfer_anguilla_to_st_barth || 0);
        cruisingArea = 'SBH/AXA Transfer';
        break;
      case 'axa_sbh':
        price = parseFloat(yachtData.transfer_anguilla_to_st_barth || 0);
        cruisingArea = 'AXA/SBH Transfer';
        break;
      case 'sxm_axa':
        price = parseFloat(yachtData.transfer_st_martin_to_anguilla || yachtData.transfer_anguilla_to_st_martin || 0);
        cruisingArea = 'SXM/AXA Transfer';
        break;
      case 'axa_sxm':
        price = parseFloat(yachtData.transfer_anguilla_to_st_martin || 0);
        cruisingArea = 'AXA/SXM Transfer';
        break;
      case 'full_day_sbh':
        price = parseFloat(yachtData.full_day_st_barth || 0);
        cruisingArea = 'Full Day 9h-17h Around St Barthélémy';
        break;
      case 'full_day_sxm':
        price = parseFloat(yachtData.full_day_st_martin || 0);
        cruisingArea = 'Full Day 9h-17h Around St Martin';
        break;
      case 'full_day_axa':
        price = parseFloat(yachtData.full_day_anguilla || 0);
        cruisingArea = 'Full Day 9h-17h Around Anguilla';
        break;
      case 'half_day_sbh_am':
        price = parseFloat(yachtData.half_day_st_barth_morning || 0);
        cruisingArea = 'Half Day 9h-13h Around St Barthélémy';
        break;
      case 'half_day_sbh_pm':
        price = parseFloat(yachtData.half_day_st_barth_afternoon || 0);
        cruisingArea = 'Half Day 13h-17h Around St Barthélémy';
        break;
      case 'sunset':
        price = parseFloat(yachtData.sunset_price || 0);
        cruisingArea = 'Sunset 17h-19h Around St Barthélémy';
        break;
      default:
        cruisingArea = '';
        break;
    }

    if (price > 0) {
      form.setFieldsValue({ boat_price: price });
      calculateTotal();
    }

    if (cruisingArea) {
      form.setFieldsValue({ cruising_area: cruisingArea });
    }
  };

  const handleYachtChange = (value) => {
    const yacht = yachts.find(y => y.key === value);
    if (yacht) {
      form.setFieldsValue({
        name_of_vessel: yacht.name || '',
      });
      updatePriceAndCruisingArea();
    }
  };

  const handleCharterTypeChange = (value) => {
    const charter = CHARTER_TYPES.find(c => c.value === value);

    const isTransferType = value && !value.startsWith('full_day_')
                           && !value.startsWith('half_day_')
                           && value !== 'sunset'
                           && value !== 'custom';
    setIsTransfer(isTransferType);

    if (charter) {
      form.setFieldsValue({
        from_hour: dayjs(charter.from, 'HH:mm'),
        to_hour: dayjs(charter.to, 'HH:mm'),
      });
    }

    if (!isTransferType) {
      form.setFieldsValue({
        flight_info: '',
      });
    }

    updatePriceAndCruisingArea();
  };

  const handleGenerate = async (values) => {
    setLoading(true);
    setResponseData(null);

    try {
      const today = dayjs().format('DD/MM/YYYY');
      const charterDate = values.charter_date ? values.charter_date.format('DD/MM/YYYY') : '';
      const fromHour = values.from_hour ? values.from_hour.format('HH:mm') : '';
      const toHour = values.to_hour ? values.to_hour.format('HH:mm') : '';

      const yacht = yachts.find(y => y.key === values.yacht);

      // Build document data object
      const documentData = {
        today_date: today,
        generation_date: today,
        charter_date: charterDate,
        from_hour: fromHour,
        to_hour: toHour,
        client_name: values.name,
        charterer: values.charterer || '',
        brand_of_vessel: yacht?.brand || '',
        name_of_vessel: values.name_of_vessel || '',
        length: yacht?.length || '',
        cruising_area: values.cruising_area,
        flight_info: values.flight_info || '',
        pax_number: values.guests_number?.toString() || '',
        boat_price: values.boat_price ? `${values.boat_price}€ (BOAT PRICE)` : '',
        transport_price: values.transport_price ? `+ ${values.transport_price}€` : '',
        transport_text: values.transport_text || '',
        taxes_price: values.taxes_price ? `+ ${values.taxes_price}€` : '',
        taxes_text: values.taxes_text || '',
        discount_price: values.discount_price ? `- ${values.discount_price}€` : '',
        discount_text: values.discount_text || '',
        extra_hour_price: values.extra_hour_price ? `+ ${values.extra_hour_price}€` : '',
        extra_hour_text: values.extra_hour_text || '',
        custom_price: values.custom_price ? `+ ${values.custom_price}€` : '',
        custom_text: values.custom_text || '',
        total_price: values.total_price ? `${values.total_price} EUR` : '',
      };

      // Use Google Drive API
      if (generationMode === 'google') {
        console.log('');
        console.log('═══════════════════════════════════════════════');
        console.log('🚀 GENERATING WITH GOOGLE DRIVE API');
        console.log('═══════════════════════════════════════════════');

        try {
          const result = await generateDocumentWithGoogleDrive(
            'Contrat',
            documentData,
            `charter_${Date.now()}`,
            'bateaux'
          );

          console.log('✅ Document generated:', result);

          setResponseData({
            status: 'success',
            mode: 'google',
            documentId: result.googleDocId,
            documentUrl: result.googleDocUrl,
            supabaseId: result.id,
          });

          message.success(
            <span>
              Document created! <a href={result.googleDocUrl} target="_blank" rel="noopener noreferrer">Open in Google Docs</a>
            </span>,
            10
          );

        } catch (googleError) {
          // Check if it's an authentication error
          if (googleError.message.startsWith('NOT_AUTHENTICATED:')) {
            const authUrl = googleError.message.replace('NOT_AUTHENTICATED:', '');
            message.warning('Please authenticate with Google first');
            window.location.href = authUrl;
            return;
          }
          throw googleError;
        }

      } else {
        // Use Make.com webhook
        // Prepare replacements array for Make.com
        const replacements = Object.entries(documentData).map(([key, value]) => ({
          find: `{{${key}}}`,
          replace: value,
        }));

        const makePayload = {
          template_id: '1-VnKfdIu5tWdr2LTFO-jdcMpwOa9-Rf7vkoHRQL3m-c',
          client_name: values.name,
          charter_date: charterDate,
          charter_id: `charter_${Date.now()}`,
          timestamp: new Date().toISOString(),
          replacements: replacements
        };

        console.log('');
        console.log('═══════════════════════════════════════════════');
        console.log('🚀 SENDING TO MAKE.COM');
        console.log('═══════════════════════════════════════════════');
        console.log('URL:', MAKE_WEBHOOK_URL);
        console.log('Template ID:', makePayload.template_id);
        console.log('Number of replacements:', makePayload.replacements.length);
        console.log('');
        console.log('Full JSON payload:');
        console.log(JSON.stringify(makePayload, null, 2));
        console.log('');

        // Send to Make.com webhook
        const response = await fetch(MAKE_WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(makePayload),
        });

        if (!response.ok) {
          throw new Error(`Make.com returned status ${response.status}: ${response.statusText}`);
        }

        // Handle both JSON and plain text responses
        const contentType = response.headers.get('content-type');
        let respData;

        if (contentType && contentType.includes('application/json')) {
          respData = await response.json();
        } else {
          const textResponse = await response.text();
          respData = {
            status: 'success',
            mode: 'make',
            message: textResponse,
            raw: textResponse
          };
        }

        console.log('✅ Make.com response:', respData);
        console.log('═══════════════════════════════════════════════');
        console.log('');

        setResponseData(respData);
        message.success('Document data sent to Make.com successfully!', 5);
      }

    } catch (error) {
      console.error('❌ Error generating document:', error);
      message.error(`Error: ${error.message}`, 10);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    }}>
      <style>
        {`
          .ant-picker-input > input,
          .ant-picker-input > input::placeholder,
          .ant-picker-input input {
            color: #2c3e50 !important;
          }
          .ant-picker-dropdown .ant-picker-cell-inner {
            color: #2c3e50 !important;
          }
          .ant-picker-dropdown .ant-picker-header button {
            color: #2c3e50 !important;
          }
          .ant-picker-dropdown .ant-picker-time-panel-column > li {
            color: #2c3e50 !important;
          }
          .ant-picker-time-panel-cell-inner {
            color: #2c3e50 !important;
          }
          .ant-form-item-label > label {
            font-size: 10px !important;
            font-weight: 600 !important;
            color: #2c3e50 !important;
          }
          .ant-input, .ant-select-selector, .ant-picker, .ant-input-number {
            font-size: 11px !important;
          }
        `}
      </style>

      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.15)',
        padding: '14px 20px',
        marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
          }}>
            <CloudUploadOutlined style={{ color: '#FFFFFF' }} />
          </div>
          <div>
            <Title level={5} style={{ margin: 0, color: '#FFFFFF', fontSize: '15px', fontWeight: 600 }}>
              Make.com Document Test
            </Title>
            <Text style={{ fontSize: '11px', color: '#FFFFFF', opacity: 0.9 }}>
              Send document data to Make.com for processing
            </Text>
          </div>
        </div>
      </div>

      <Card
        style={{
          border: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
        }}
        styles={{ body: { padding: '20px' } }}
      >
        {/* Generation Mode Selector */}
        <Card
          size="small"
          style={{ marginBottom: '16px', border: '1px solid #d9d9d9' }}
          styles={{ body: { padding: '12px' } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <Text strong style={{ fontSize: '12px' }}>Generation Mode:</Text>
            </div>
            <Space>
              <Button
                type={generationMode === 'make' ? 'primary' : 'default'}
                icon={<CloudUploadOutlined />}
                onClick={() => setGenerationMode('make')}
                size="small"
              >
                Make.com
              </Button>
              <Button
                type={generationMode === 'google' ? 'primary' : 'default'}
                icon={<GoogleOutlined />}
                onClick={() => setGenerationMode('google')}
                size="small"
                style={generationMode === 'google' ? { background: '#4285f4', borderColor: '#4285f4' } : {}}
              >
                Google Drive API
              </Button>
            </Space>
            {generationMode === 'google' && (
              <div>
                {googleStatus.authenticated ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">Google Connected</Tag>
                ) : (
                  <Tag color="warning">Not Connected</Tag>
                )}
              </div>
            )}
          </div>
        </Card>

        {generationMode === 'make' ? (
          <Alert
            message="Make.com Integration"
            description={
              <div>
                <p style={{ fontSize: '11px', marginBottom: '8px' }}>
                  Sends data to Make.com webhook for document generation via Google Docs.
                </p>
                <p style={{ fontSize: '11px', margin: 0 }}>
                  <strong>Webhook URL:</strong> {MAKE_WEBHOOK_URL}
                </p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: '16px', fontSize: '11px' }}
          />
        ) : (
          <Alert
            message="Google Drive API (Direct)"
            description={
              <div>
                <p style={{ fontSize: '11px', marginBottom: '8px' }}>
                  Generate documents directly using Google Drive API with OAuth.
                </p>
                {!googleStatus.authenticated && (
                  <Button
                    type="primary"
                    icon={<GoogleOutlined />}
                    size="small"
                    onClick={() => window.location.href = getGoogleAuthUrl()}
                    style={{ background: '#4285f4', borderColor: '#4285f4' }}
                  >
                    Connect Google Account
                  </Button>
                )}
              </div>
            }
            type={googleStatus.authenticated ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: '16px', fontSize: '11px' }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerate}
          onValuesChange={calculateTotal}
          initialValues={{
            boat_price: 0,
            transport_price: 0,
            transport_text: '',
            taxes_price: 0,
            taxes_text: '',
            discount_price: 0,
            discount_text: '',
            extra_hour_price: 0,
            extra_hour_text: '',
            custom_price: 0,
            custom_text: '',
            total_price: 0,
          }}
        >
          <Row gutter={[8, 0]}>
            <Col span={8}>
              <Form.Item
                name="name"
                label="Client Name"
                rules={[{ required: true, message: 'Required' }]}
                style={{ marginBottom: 8 }}
              >
                <Input placeholder="John Doe" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="charterer" label="Hotel / Agency" style={{ marginBottom: 2 }}>
                <Input
                  placeholder="Hotel Christopher"
                  disabled={isDirectCharterer}
                  style={{ height: '32px', borderRadius: '6px' }}
                />
              </Form.Item>
              <Checkbox
                checked={isDirectCharterer}
                onChange={(e) => {
                  setIsDirectCharterer(e.target.checked);
                  form.setFieldsValue({ charterer: e.target.checked ? 'direct' : '' });
                }}
                style={{ fontSize: '10px', marginTop: 0 }}
              >
                Direct
              </Checkbox>
            </Col>

            <Col span={4}>
              <Form.Item
                name="charter_date"
                label="Date"
                rules={[{ required: true, message: 'Req' }]}
                style={{ marginBottom: 8 }}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="guests_number"
                label="Pax"
                rules={[{ required: true, message: 'Req' }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  min={1}
                  max={20}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[8, 0]}>
            <Col span={6}>
              <Form.Item
                name="charter_type"
                label="Charter Type"
                rules={[{ required: true, message: 'Req' }]}
                style={{ marginBottom: 8 }}
              >
                <Select
                  placeholder="Select"
                  onChange={handleCharterTypeChange}
                  style={{ borderRadius: '6px' }}
                  showSearch={false}
                >
                  {CHARTER_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item name="from_hour" label="From" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 8 }}>
                <TimePicker
                  format="HH:mm"
                  minuteStep={30}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="to_hour" label="To" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 8 }}>
                <TimePicker
                  format="HH:mm"
                  minuteStep={30}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="yacht"
                label="Yacht"
                rules={[{ required: true, message: 'Req' }]}
                style={{ marginBottom: 8 }}
              >
                <Select
                  placeholder="Select"
                  onChange={handleYachtChange}
                  style={{ borderRadius: '6px' }}
                  showSearch
                  filterOption={(input, option) => {
                    const text = `${option.children}`;
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {yachts.map(yacht => (
                    <Option key={yacht.key} value={yacht.key}>
                      {yacht.brand} {yacht.length}ft
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="name_of_vessel" label="Name" style={{ marginBottom: 8 }}>
                <Input
                  readOnly
                  style={{
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: '#f0f0f0',
                    color: '#000'
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="cruising_area" label="Zone" style={{ marginBottom: 8 }}>
                <Input placeholder="Auto" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>

            {isTransfer && (
              <Col span={24}>
                <Form.Item name="flight_info" label="Flight Info" style={{ marginBottom: 8 }}>
                  <Input placeholder="AF123 / AA456" style={{ height: '32px', borderRadius: '6px' }} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider style={{ margin: '12px 0 12px 0', fontSize: '11px', fontWeight: 600 }}>
            <CalculatorOutlined style={{ marginRight: '6px' }} />
            Pricing
          </Divider>

          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '12px',
            justifyContent: 'flex-end',
          }}>
            <Button
              onClick={autoCalculateVIP}
              size="small"
              style={{
                fontSize: '10px',
                height: '28px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                border: 'none',
              }}
            >
              Auto VIP
            </Button>
            <Button
              onClick={autoCalculateTaxi}
              size="small"
              style={{
                fontSize: '10px',
                height: '28px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                border: 'none',
              }}
            >
              Auto Taxi
            </Button>
            <Button
              onClick={autoCalculateTaxes}
              size="small"
              style={{
                fontSize: '10px',
                height: '28px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                border: 'none',
              }}
            >
              Auto Taxes
            </Button>
          </div>

          <Row gutter={[6, 0]}>
            <Col span={4}>
              <Form.Item
                name="boat_price"
                label="Boat"
                rules={[{ required: true, message: 'Req' }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                  onChange={calculateTotal}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>

            <Col span={2}>
              <Form.Item name="transport_price" label="Transport" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                  placeholder="0"
                  onChange={calculateTotal}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="transport_text" label=" " style={{ marginBottom: 8 }}>
                <Input placeholder="VIP/Taxi" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>

            <Col span={2}>
              <Form.Item name="taxes_price" label="Taxes" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                  placeholder="0"
                  onChange={calculateTotal}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="taxes_text" label=" " style={{ marginBottom: 8 }}>
                <Input placeholder="SXM" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>

            <Col span={2}>
              <Form.Item name="discount_price" label="Discount" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                  placeholder="0"
                  onChange={calculateTotal}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="discount_text" label=" " style={{ marginBottom: 8 }}>
                <Input placeholder="Text" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>

            <Col span={2}>
              <Form.Item name="extra_hour_price" label="Extra" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                  placeholder="0"
                  onChange={calculateTotal}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="extra_hour_text" label=" " style={{ marginBottom: 8 }}>
                <Input placeholder="Text" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[6, 0]}>
            <Col span={2}>
              <Form.Item name="custom_price" label="Custom" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  style={{ width: '100%', height: '32px', borderRadius: '6px' }}
                  placeholder="0"
                  onChange={calculateTotal}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="custom_text" label=" " style={{ marginBottom: 8 }}>
                <Input placeholder="Description" style={{ height: '32px', borderRadius: '6px' }} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="total_price" label="TOTAL" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  style={{
                    width: '100%',
                    height: '38px',
                    borderRadius: '8px',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#1b5e20'
                  }}
                  addonAfter="EUR"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: '16px', marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<CloudUploadOutlined />}
              block
              style={{
                height: '44px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              }}
            >
              Send to Make.com
            </Button>
          </Form.Item>
        </Form>

        {responseData && (
          <Alert
            message="Make.com Response"
            description={
              <pre style={{
                fontSize: '10px',
                margin: 0,
                maxHeight: '300px',
                overflow: 'auto',
                background: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px'
              }}>
                {JSON.stringify(responseData, null, 2)}
              </pre>
            }
            type="success"
            showIcon
            style={{ marginTop: '16px', fontSize: '11px' }}
          />
        )}
      </Card>
    </div>
  );
}

export default MakeDocumentTest;
